import { Box, Grid, Group, Image, Paper, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import type { NavigateOptions } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import { createFacetedMinMaxValues, useTable } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { Calendar, ContactCenter, NewOrder, Search, SupportIcon } from '@apple/assets/icons';
import { SearchImage } from '@apple/assets/images';
import { Trans, useTranslation } from '@apple/lib/i18next';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { Link } from '@apple/ui/link/Link';
import type { DashboardCard } from '@apple/ui/shell/components/Dashboard';
import { Dashboard } from '@apple/ui/shell/components/Dashboard';
import { getEnv } from '@apple/utils/config/env';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';

import { useUserAnnouncements } from '@apple/features/announcements/hooks/useUserAnnouncements';
import { UserAnnouncements } from '@apple/features/announcements-channel/components/UserAnnouncements';
import { OrderCommentStatusDisplay } from '@apple/features/order/components/order-comment-status';
import { OrderStatusDisplay } from '@apple/features/order/components/status';
import type {
	OrderSearchFilters,
	OrderSearchListing,
} from '@apple/features/order-channel/models/search';
import { getOrderSearchQueryOptions } from '@apple/features/order-channel/queries/search';

import styles from './user.module.css';

const features = createDataTableFeatures({
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

const env = getEnv();
const defaultSorting: SortingState = [{ id: 'orderData', desc: true }];
const defaultFilters = {
	startDate: dayjs().subtract(180, 'days').startOf('day').toDate(),
};

type Props = {
	search: Record<string, string | undefined>;
	navigate: (params: NavigateOptions) => Promise<void>;
	cartItemCount?: number;
};

export function UserHomePage({ search, navigate, cartItemCount }: Props) {
	const { t } = useTranslation('user-home');
	const userAnnouncements = useUserAnnouncements();
	const theme = useMantineTheme();

	const tableState = useTableState<OrderSearchListing, OrderSearchFilters>({
		search,
		navigate,
		defaultFilters,
		defaultSorting,
		fieldMap: [],
		defaultPageSize: 20,
	});

	const orderSearch = useQuery(
		getOrderSearchQueryOptions(tableState.state.pagination, tableState.currentFilterData),
	);

	const table = useTable({
		features,
		data: orderSearch.data.rows,
		rowCount: orderSearch.data.totalRowCount,
		...tableState,
		enableFilters: false,
		enableColumnFilters: false,
		enableSorting: true,
		manualFiltering: false,
		manualSorting: false,
		manualPagination: true,
		columns: [
			{
				id: 'orderNumber',
				size: 10,
				accessorKey: 'purchaseOrderNumber',
				header: t('order:searchResults.table.columns.poNumber'),
				cell: ({ row }) => (
					<Link
						to='/orders/$poNumber'
						params={{ poNumber: row.original.purchaseOrderNumber }}
						title={t('order:searchResults.table.links.viewOrderDetails')}
					>
						<Text size='sm'>{row.original.purchaseOrderNumber}</Text>
					</Link>
				),
			},
			{
				header: t('order:searchResults.table.columns.orderDate'),
				size: 10,
				accessorKey: 'orderDate',
				cell: ({ row }) => (
					<FormattedDate value={row.original.orderDate} humanize size='sm' />
				),
			},
			{
				size: 10,
				header: t('order:searchResults.table.columns.appleId'),
				accessorKey: 'appleId',
			},
			{
				id: 'orderStatus',
				accessorKey: 'status',
				header: t('order:searchResults.table.columns.status'),
				cell: ({ row }) => (
					<OrderStatusDisplay
						status={row.original.status}
						isOrderMarkedForCancellation={row.original.isOrderMarkedForCancellation}
						size='sm'
					/>
				),
			},
			{
				id: 'orderStatusIcon',
				size: 50,
				cell: ({ row }) => {
					return (
						<>
							{row.original.hasComments ? (
								<OrderCommentStatusDisplay
									purchaseOrderNumber={row.original.purchaseOrderNumber}
									orderCommentStatus={row.original.orderCommentStatus}
									compact
								/>
							) : null}
						</>
					);
				},
			},
		],
	});

	const hasItems = useMemo(() => (cartItemCount ?? 0) > 0, [cartItemCount]);

	const cards = useMemo(
		() =>
			[
				{
					title: hasItems
						? t('cards.viewBag', { quantity: cartItemCount })
						: t('cards.shop'),
					icon: NewOrder,
					to: hasItems ? '/cart/$step' : '/cart',
					params: hasItems ? { step: 'review' } : undefined,
					datatestId: 'create-new-order',
					color: '#61bb46',
				},
				{
					title: t('cards.help'),
					icon: SupportIcon,
					to: '/help',
					datatestId: 'support-request',
					color: '#fdb827',
				},
				{
					title: t('cards.warehouseCalendar'),
					icon: Calendar,
					href: `${env.APPLE_CDN_URL}/filemanager/2024 Global Holiday Calendar-961054071.pdf`,
					datatestId: 'calendar',
					target: '_blank',
					color: '#f5821f',
				},
				{
					title: t('cards.contactCenterNumbers'),
					icon: ContactCenter,
					href: 'https://acs-landing.apple.com/landing/?',
					datatestId: 'contact-center',
					target: '_blank',
					color: '#e03a3e',
				},
			] satisfies DashboardCard[],
		[cartItemCount, hasItems, t],
	);

	function renderEmpty() {
		return (
			<Stack align='center'>
				<Title order={3}>{t('noOrdersSixMonths')}</Title>
				<Image radius='lg' src={SearchImage} w={150} fit='contain' />
				<Group>
					<Title order={3}>
						<Trans
							t={t}
							i18nKey='visitOrderHistory'
							components={{
								OrderHistoryLink: (
									<Link
										className={styles.forceInline}
										size='xl'
										to='/orders/search'
									/>
								),
							}}
						/>
					</Title>
				</Group>
			</Stack>
		);
	}
	return (
		<Grid gap={{ base: 5, xs: 'md', md: 'xl', xl: 10 }}>
			<Grid.Col span={{ base: 12, lg: 6 }}>
				<Stack>
					<Dashboard cards={cards} py='sm' px='md' mx='xl' />
					{!userAnnouncements.isLoading &&
						!userAnnouncements.error &&
						userAnnouncements.data && (
							<Box py='sm' px='md' mx='xl'>
								<UserAnnouncements announcementList={userAnnouncements.data} />
							</Box>
						)}
				</Stack>
			</Grid.Col>
			<Grid.Col span={{ base: 12, lg: 6 }}>
				<Stack>
					<Group justify='space-between'>
						<Title px='md' order={4}>
							{t('recentOrders')}
						</Title>
						<Link to='/orders/search' title='Search all orders' mx='md'>
							<Search c='bright' />
						</Link>
					</Group>
					<Paper mx='md' p='sm' withBorder>
						<DataTable
							minWidth={theme.other.page.minWidth}
							hidePageSize
							hidePagination
							table={table}
							loading={userAnnouncements.isFetching}
							renderEmpty={renderEmpty}
						/>
					</Paper>
				</Stack>
			</Grid.Col>
		</Grid>
	);
}
